/**
 * Labor Utilization Report (20.6) — user hours table, job breakdown drill-down, chart.
 */

import { useCallback, useEffect, useState } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { fetchLaborUtilizationReport, exportLaborUtilizationCSV, exportLaborUtilizationPDF } from '../../api/reports';
import { LoadingSpinner, EmptyState } from '../common';
import ReportShell, { DateRangeFilter } from './ReportShell';

export default function LaborUtilizationReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [exporting, setExporting] = useState(null);

  const params = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    user_id: userFilter || undefined,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchLaborUtilizationReport(params)); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, userFilter]);

  useEffect(() => { load(); }, [load]);

  // Load drill-down for a specific user
  async function loadUserBreakdown(userId) {
    if (expandedUser === userId) { setExpandedUser(null); return; }
    setExpandedUser(userId);
    try {
      const detail = await fetchLaborUtilizationReport({ ...params, user_id: userId });
      const userRow = detail?.rows?.[0];
      if (userRow?.job_breakdown) {
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            rows: prev.rows.map((r) =>
              r.user_id === userId ? { ...r, job_breakdown: userRow.job_breakdown } : r
            ),
          };
        });
      }
    } catch { /* ignore */ }
  }

  async function handleExport(type) {
    setExporting(type);
    try {
      if (type === 'csv') await exportLaborUtilizationCSV(params);
      else await exportLaborUtilizationPDF(params);
    } catch {} finally { setExporting(null); }
  }

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const chartData = rows.slice(0, 15).map((r) => ({
    name: (r.user_name || r.user_email || '').slice(0, 15),
    clock: parseFloat(r.clock_hours) || 0,
    manual: parseFloat(r.manual_hours) || 0,
    utilization: parseFloat(r.utilization_pct) || 0,
  }));

  return (
    <ReportShell
      title="Labor Utilization"
      exporting={exporting}
      onExportCSV={() => handleExport('csv')}
      onExportPDF={() => handleExport('pdf')}
      filters={
        <>
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChangeFrom={setDateFrom} onChangeTo={setDateTo} />
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">User ID</label>
            <input
              type="text"
              className="border border-gray-300 rounded-md px-2 py-1 text-sm w-48"
              placeholder="Filter by user..."
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            />
          </div>
          <button onClick={load} className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 self-end">Run</button>
        </>
      }
    >
      {loading ? <div className="flex justify-center py-10"><LoadingSpinner /></div> : rows.length === 0 ? (
        <EmptyState title="No labor data" description="Time entries will appear after builders clock in." />
      ) : (
        <>
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-3 py-2" />
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2 text-right">Total Hours</th>
                  <th className="px-3 py-2 text-right">Clock</th>
                  <th className="px-3 py-2 text-right">Manual</th>
                  <th className="px-3 py-2 text-right">Entries</th>
                  <th className="px-3 py-2 text-right">Available</th>
                  <th className="px-3 py-2 text-right">Utilization</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.user_id} className="group">
                    <td colSpan={8} className="p-0">
                      <table className="w-full">
                        <tbody>
                          <tr
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => loadUserBreakdown(r.user_id)}
                          >
                            <td className="px-3 py-2 w-8 text-gray-400">
                              <span className={`inline-block transition-transform ${expandedUser === r.user_id ? 'rotate-90' : ''}`}>&#9654;</span>
                            </td>
                            <td className="px-3 py-2 font-medium text-foreground">{r.user_name || r.user_email}</td>
                            <td className="px-3 py-2 text-right font-medium">{parseFloat(r.total_hours).toFixed(1)}</td>
                            <td className="px-3 py-2 text-right text-gray-500">{parseFloat(r.clock_hours).toFixed(1)}</td>
                            <td className="px-3 py-2 text-right text-gray-500">{parseFloat(r.manual_hours).toFixed(1)}</td>
                            <td className="px-3 py-2 text-right text-gray-500">{r.entry_count}</td>
                            <td className="px-3 py-2 text-right text-gray-400">{parseFloat(r.available_hours).toFixed(0)}h</td>
                            <td className="px-3 py-2 text-right">
                              <span className={
                                parseFloat(r.utilization_pct) >= 80 ? 'text-green-600 font-medium' :
                                parseFloat(r.utilization_pct) >= 50 ? 'text-yellow-600' : 'text-red-600'
                              }>
                                {r.utilization_pct}%
                              </span>
                            </td>
                          </tr>
                          {/* Job breakdown drill-down */}
                          {expandedUser === r.user_id && r.job_breakdown && (
                            <tr>
                              <td colSpan={8} className="px-6 py-2 bg-gray-50">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-gray-400">
                                      <th className="text-left py-1">Job #</th>
                                      <th className="text-right py-1">Hours</th>
                                      <th className="text-right py-1">Entries</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.job_breakdown.map((j) => (
                                      <tr key={j.job_id} className="border-t border-gray-100">
                                        <td className="py-1 font-mono">{j.job_number}</td>
                                        <td className="py-1 text-right">{parseFloat(j.hours).toFixed(1)}</td>
                                        <td className="py-1 text-right">{j.entries}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-3 py-2" colSpan={2}>Total</td>
                  <td className="px-3 py-2 text-right">{parseFloat(totals.total_hours || 0).toFixed(1)}</td>
                  <td className="px-3 py-2" colSpan={2} />
                  <td className="px-3 py-2 text-right">{totals.total_entries}</td>
                  <td className="px-3 py-2" colSpan={2} />
                </tr>
              </tbody>
            </table>
          </div>

          {chartData.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Hours by User</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="clock" stackId="hours" fill="#3b82f6" name="Clock Hours" />
                  <Bar yAxisId="left" dataKey="manual" stackId="hours" fill="#93c5fd" name="Manual Hours" />
                  <Line yAxisId="right" type="monotone" dataKey="utilization" stroke="#10b981" name="Utilization %" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </ReportShell>
  );
}
